import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from './use-auth';
import apiClient from '@/lib/api-client';
import { useQuery } from '@tanstack/react-query';

/**
 * Shared hook to fetch subscription status with consistent caching
 * 
 * Uses a 5-minute cache that's shared across the entire application.
 * All components using this hook will share the same cached data.
 */
export function useSubscriptionStatus() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['subscription', 'status', user?.companyId],
    queryFn: async () => {
      if (!user?.companyId) {
        return null;
      }
      // Let errors surface to the query - don't swallow them
      // This allows the hook to distinguish between "no subscription" and "error checking"
      const response = await apiClient.billing.getBillingStripeSubscriptionStatusFromStripe();
      return response;
    },
    enabled: !!user?.companyId,
    retry: 1, // Retry once for transient network errors
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to check subscription status and redirect if not active
 * 
 * Uses existing subscription status endpoint and follows wouter navigation pattern
 */
export function useSubscriptionGuard(options?: { skipRedirect?: boolean }) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const { data: status, isLoading, error } = useSubscriptionStatus();

  useEffect(() => {
    // Don't redirect if skipRedirect option is set
    if (options?.skipRedirect) {
      return;
    }

    // Don't redirect while loading or if no user
    if (isLoading || !user) {
      return;
    }

    // Don't redirect if there was an error fetching status
    // This prevents blocking paid users due to transient network/5xx errors
    if (error) {
      console.warn('Cannot verify subscription status due to error - allowing access:', error);
      return;
    }

    // Check if subscription is active
    const hasActiveSubscription = status?.hasActiveSubscription === true;

    if (!hasActiveSubscription) {
      // Redirect to settings plans tab
      setLocation('/settings?tab=plans');
    }
  }, [status, isLoading, user, setLocation, options?.skipRedirect, error]);

  return {
    hasActiveSubscription: status?.hasActiveSubscription ?? false,
    hasPaymentMethod: status?.hasPaymentMethod ?? false,
    isLoading,
    error,
  };
}

/**
 * Shared gate for pay-as-you-go features (comparisons/supplements) that require a
 * payment method on file.
 *
 * Centralizes the "should we show the paywall?" decision so every call site
 * behaves identically. We only paywall when the subscription status was fetched
 * successfully AND it *confirms* there's no card on file (`hasPaymentMethod`
 * is explicitly `false`).
 *
 * Any "unknown" state must NOT paywall: while loading, on a transient
 * network/5xx error, or when the query is disabled (e.g. `user`/`companyId`
 * not loaded yet) the status is `undefined`. Treating that as "no card" would
 * falsely block users, so we fall back to "no paywall" and rely on the backend
 * to enforce payment.
 *
 * To keep callers from collapsing "unknown" into "confirmed false", the payment
 * method state is exposed as a tri-state (`hasPaymentMethod: boolean | undefined`)
 * alongside an explicit `paymentMethodKnown` flag. Only when
 * `paymentMethodKnown` is `true` can `hasPaymentMethod` be trusted as a
 * confirmed answer; otherwise the status is simply not known yet.
 *
 * @returns `shouldPaywall` plus the underlying status flags for callers that need them.
 */
export function usePaymentMethodGate() {
  const { data: status, isLoading, error } = useSubscriptionStatus();

  // The status was fetched successfully and carries a concrete payment-method
  // answer. `status` is `undefined` while loading, on error, or when the query
  // is disabled — none of which are a known "payment method" state.
  const paymentMethodKnown =
    !isLoading && !error && status?.hasPaymentMethod !== undefined;

  // Only paywall on a confirmed missing card, i.e. when the answer is known
  // *and* explicitly `false`.
  const shouldPaywall = paymentMethodKnown && status?.hasPaymentMethod === false;

  return {
    shouldPaywall,
    // Tri-state: `true`/`false` only meaningful when `paymentMethodKnown` is
    // `true`; `undefined` means the status isn't known yet (loading/error/disabled).
    hasPaymentMethod: paymentMethodKnown ? status?.hasPaymentMethod : undefined,
    paymentMethodKnown,
    isLoading,
    error,
  };
}
